/**
 * [INPUT]: 依赖 supabase Auth、VITE_ADMIN_EMAILS 白名单
 * [OUTPUT]: 对外提供 AuthProvider（登录门 + STATIC_MENUS）
 * [POS]: providers 认证状态源；未登录/非白名单一律拒绝
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/services/supabase'
import { AuthContext } from './AuthContext'

const STATIC_MENUS = [
  {
    menuId: 'dashboard',
    menuName: '控制台',
    path: '/admin/dashboard',
    icon: 'dashboard',
    visible: '0',
    orderNum: 0,
  },
  {
    menuId: 'zinner',
    menuName: 'Zinner 运营',
    icon: 'users',
    visible: '0',
    orderNum: 10,
    children: [
      { menuId: 'users', menuName: '用户', path: '/zinner/users', icon: 'users', visible: '0' },
      { menuId: 'prospects', menuName: '对象', path: '/zinner/prospects', icon: 'user', visible: '0' },
      { menuId: 'date-logs', menuName: '往来', path: '/zinner/date-logs', icon: 'calendar', visible: '0' },
      { menuId: 'topics', menuName: '话题', path: '/zinner/topics', icon: 'message', visible: '0' },
      { menuId: 'sms', menuName: '短信漏斗', path: '/zinner/sms', icon: 'mail', visible: '0' },
    ],
  },
]

function adminEmails() {
  return String(import.meta.env.VITE_ADMIN_EMAILS || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
}

function isAdminEmail(email) {
  if (!email) return false
  const list = adminEmails()
  if (list.length === 0) return false
  return list.includes(String(email).toLowerCase())
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const menus = useMemo(() => STATIC_MENUS, [])

  const rejectNonAdmin = useCallback(async (sess) => {
    const email = sess?.user?.email
    if (!isAdminEmail(email)) {
      await supabase?.auth.signOut()
      setSession(null)
      setUser(null)
      const err = new Error('你不是管理员')
      err.code = 'NOT_ADMIN'
      throw err
    }
    setSession(sess)
    setUser({
      id: sess.user.id,
      email: sess.user.email,
      nickname: sess.user.email?.split('@')[0] || 'Admin',
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        if (!supabase) {
          setLoading(false)
          return
        }
        const { data } = await supabase.auth.getSession()
        if (cancelled) return
        if (data.session) {
          try {
            await rejectNonAdmin(data.session)
          } catch {
            /* already cleared */
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    const { data: sub } = supabase?.auth.onAuthStateChange(async (_event, sess) => {
      if (!sess) {
        setSession(null)
        setUser(null)
        return
      }
      try {
        await rejectNonAdmin(sess)
      } catch (e) {
        if (e?.code === 'NOT_ADMIN') toast.error('你不是管理员，无权登录后台')
      }
    }) || { data: { subscription: null } }

    return () => {
      cancelled = true
      sub?.subscription?.unsubscribe?.()
    }
  }, [rejectNonAdmin])

  const signIn = useCallback(
    async (email, password) => {
      if (!supabase) throw new Error('Auth 未配置')
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
      await rejectNonAdmin(data.session)
      return data
    },
    [rejectNonAdmin],
  )

  const signOut = useCallback(async () => {
    await supabase?.auth.signOut()
    setSession(null)
    setUser(null)
  }, [])

  const value = {
    user,
    session,
    menus,
    loading,
    isAuthenticated: Boolean(user && session),
    signIn,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
